import { useId, type ReactNode } from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../utils/cn';

/** The lower and upper bound, in that order. */
export type RangeSliderValue = [number, number];

export type RangeSliderProps = {
  value: RangeSliderValue;
  min: number;
  max: number;
  step: number;
  /** Fires on every move while a thumb is dragged or stepped. */
  onValueChange: (value: RangeSliderValue) => void;
  /** Fires once, when a thumb is released or a key press ends. */
  onValueCommit?: (value: RangeSliderValue) => void;
  /** Names the slider for assistive technology; each thumb adds its bound. */
  label: string;
  formatValue?: (value: number) => string;
  /** A short line above the track saying what the scale measures. */
  caption?: ReactNode;
  /**
   * A sentence under the track saying what the range selects. It follows
   * the thumbs as they move and is announced politely.
   */
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
  dataTestId?: string;
};

const toRangeSliderValue = (
  values: number[],
  fallback: RangeSliderValue,
): RangeSliderValue => [values[0] ?? fallback[0], values[1] ?? fallback[1]];

const THUMB_CLASS_NAME = cn(
  'block size-4 rounded-full border-2 border-action-primary bg-white shadow-sm',
  'focus-visible:ring-2 focus-visible:ring-action-primary focus-visible:ring-offset-2 focus-visible:outline-none',
  'data-[disabled]:border-action-primary-disabled dark:bg-gray-900',
);

/**
 * A two-thumb slider for picking a numeric range. The thumbs may meet, so a
 * single value is a range with equal bounds.
 */
export const RangeSlider = ({
  value,
  min,
  max,
  step,
  onValueChange,
  onValueCommit,
  label,
  formatValue = String,
  caption,
  description,
  disabled,
  className,
  dataTestId,
}: RangeSliderProps) => {
  const { t } = useTranslation('designSystem');
  const captionId = useId();
  const descriptionId = useId();
  const [lower, upper] = value;
  const describedBy =
    [
      caption === undefined ? undefined : captionId,
      description === undefined ? undefined : descriptionId,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div
      className={cn('flex w-full flex-col gap-1', className)}
      data-testid={dataTestId}
    >
      {caption !== undefined && (
        <p
          id={captionId}
          className={cn('text-content-muted text-xs', disabled && 'opacity-80')}
        >
          {caption}
        </p>
      )}
      <div className="flex w-full items-center gap-3 text-sm">
        <span
          aria-hidden
          className={cn(
            'w-10 shrink-0 text-right font-semibold tabular-nums',
            disabled && 'text-content-muted',
          )}
        >
          {formatValue(lower)}
        </span>
        <SliderPrimitive.Root
          value={value}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          onValueChange={(values) =>
            onValueChange(toRangeSliderValue(values, value))
          }
          onValueCommit={(values) =>
            onValueCommit?.(toRangeSliderValue(values, value))
          }
          className="relative flex h-5 min-w-0 flex-1 touch-none items-center select-none data-[disabled]:cursor-not-allowed"
        >
          <SliderPrimitive.Track className="bg-light-gray relative h-1.5 grow overflow-hidden rounded-full dark:bg-gray-700">
            <SliderPrimitive.Range className="bg-action-primary data-[disabled]:bg-action-primary-disabled absolute h-full rounded-full" />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb
            aria-label={t('rangeSliderMinimum', { label })}
            aria-valuetext={formatValue(lower)}
            aria-describedby={describedBy}
            className={THUMB_CLASS_NAME}
          />
          <SliderPrimitive.Thumb
            aria-label={t('rangeSliderMaximum', { label })}
            aria-valuetext={formatValue(upper)}
            aria-describedby={describedBy}
            className={THUMB_CLASS_NAME}
          />
        </SliderPrimitive.Root>
        <span
          aria-hidden
          className={cn(
            'w-10 shrink-0 font-semibold tabular-nums',
            disabled && 'text-content-muted',
          )}
        >
          {formatValue(upper)}
        </span>
      </div>
      {description !== undefined && (
        <p
          id={descriptionId}
          aria-live="polite"
          className={cn('text-content-muted text-xs', disabled && 'opacity-80')}
        >
          {description}
        </p>
      )}
    </div>
  );
};
